import styled from "styled-components";
import { removeUploadsFromUrl } from "./DizeeComponent/DixeeInput2";
import { BACKEND_URL } from "../utils/axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useEffect, useState, useRef } from "react";
import Line2 from "../svg/Line2";
import { useDispatch } from "react-redux";
import { clearAlerts } from "../store/alert/alertSlice";
import { logoutThunk } from "../store/auth/authThunk";
import UsageBar from "./UsageBar/UsageBar";
import dizeeLogo from "../assets/images/dizee logo.svg";
import { setWholeFocusCustom, setWholeFocusEvent, setWholeFocusMusic, setWholeFocusNewsletter, setWholeFocusProduct } from "../store/focuspage/focuspageSlice";
import { getConversationHistoryForDizee, renameConversation, deleteConversation } from "../pages/Manager/api/openaiService";
import { setChatHistory, openChat, closeChat } from "../store/chat/chatSlice";
import { setChatHistoryThunk } from "../store/chat/chatThunk";
import { promptsApi } from "../utils/promptsApi";
import { submitFeedbackThunk } from "../store/feedback/feedbackThunk";
import { setErrorAlert, setSuccessAlert } from "../store/alert/alertSlice";
import { useLocation } from 'react-router-dom';
import useTeamHubNavigation from '../hooks/useTeamHubNavigation';
import LoadingScreen from './LoadingScreen';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import { getUnviewedRemindersCount, getActiveRemindersCount } from '../utils/remindersData';
import AIModeModal from './AIModeModal';

const StyledDropdownItem = styled.div`
  padding: 5px 10px;
  font-size: 12px;
  color: white;
  transition: color 0.2s;
  cursor: pointer;
  
  &:hover {
    color: #FF4500;
  }
`;

const BetaBadge = styled.span`
  background: #FF2300;
  color: white;
  font-size: 8px;
  font-weight: 500;
  padding: 2px 5px;
  border-radius: 4px;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
`;


const Logo = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: white;
  letter-spacing: 1px;
  cursor: pointer;
`;


const ActiveDot = styled.div`
  width: 8px;
  height: 8px;
  background-color:${({ color }) => color || '#FF2300'};
  border-radius: 50%;
  margin-right: 5px;
`;

const LogoImage = styled.img`
  width: 50px;
  height: auto;
  opacity: 0.95;
`;

const HeaderBar = (props) => {
    const { isHome, activeTab, isAppsViewExpanded, onAppsViewToggle } = props;
    
    // All hooks must be called first, before any conditional returns
    const { isNavigating, returnToTeamHub } = useTeamHubNavigation();
    const dispatch = useDispatch();
    const { userArticle } = useSelector(state => state.user);
    const navigate = useNavigate();
    const location = useLocation();
    const { focusData } = useSelector(state => state.focuspage);
    
    const token = localStorage.getItem('dizeeToken');
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('dizeeUser')))
    const [focusPageList, setFocusPageList] = useState([]);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [conversationHistory, setConversationHistory] = useState([]);
    const [customPrompts, setCustomPrompts] = useState([]);
    const [selectedArtist, setSelectedArtist] = useState(localStorage.getItem('selectedArtist'));
    
    // Refs
    const sidebarRef = useRef(null);
    
    // Feedback state
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
    
    // Accessibility state
    const [accessibilitySettings, setAccessibilitySettings] = useState({
        fontSize: 'medium',
        highContrast: false,
        dyslexicFont: false,
        theme: 'dark',
        reduceMotion: false,
    });
    const [isAccessibilityExpanded, setIsAccessibilityExpanded] = useState(false);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    
    // AI Mode state
    const [aiModeEnabled, setAiModeEnabled] = useState(() => {
        const saved = localStorage.getItem('dizeeAIModeEnabled');
        return saved !== null ? JSON.parse(saved) : true; // Default to enabled
    });
    const [showAIModeModal, setShowAIModeModal] = useState(false);
    const [pendingAIModeChange, setPendingAIModeChange] = useState(null);
    
    // Reminders state
    const [unviewedRemindersCount, setUnviewedRemindersCount] = useState(0);
    const [activeRemindersCount, setActiveRemindersCount] = useState(0);
    
    // Collapsible sections state
    const [isConversationHistoryExpanded, setIsConversationHistoryExpanded] = useState(false);
    const [isConversationHistoryLoading, setIsConversationHistoryLoading] = useState(false);
    const [isProfileMenuExpanded, setIsProfileMenuExpanded] = useState(false);
    const [isCustomPromptsExpanded, setIsCustomPromptsExpanded] = useState(false);

    const [isAppsExpanded, setIsAppsExpanded] = useState(false);
    
    // Conversation management state
    const [renamingConversationId, setRenamingConversationId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    
    // Function to refresh conversation history
    const refreshConversationHistory = async () => {
        try {
            console.log('🔄 Manually refreshing conversation history...');
            setIsConversationHistoryLoading(true);
            
            let { success, conversations, error } = await getConversationHistoryForDizee();
            
            if (success) {
                console.log('✅ Conversation history refreshed:', conversations?.length || 0, 'items');
                
                // Force React to detect the change by creating a new array reference
                const newConversations = Array.isArray(conversations) ? [...conversations] : [];
                setConversationHistory(newConversations);
                
                console.log('🔄 Force updating conversation state with new array reference');
                
                // Auto-expand if we have conversations
                if (newConversations.length > 0) {
                    setIsConversationHistoryExpanded(true);
                }
            } else {
                console.error('❌ Failed to refresh conversation history:', error);
            }
        } catch (error) {
            console.error('💥 Error refreshing conversation history:', error);
        } finally {
            setIsConversationHistoryLoading(false);
        }
        };
    
    // Conversation management functions
    const handleRenameConversation = async (conversationId, newTitle) => {
        try {
            if (!newTitle || newTitle.trim() === '') {
                console.log('❌ Empty title, canceling rename');
                cancelRename();
                return;
            }
            
            console.log('🏷️ Renaming conversation:', conversationId, 'to:', newTitle);
            const result = await renameConversation(conversationId, newTitle.trim());
            
            if (result.success) {
                console.log('✅ Conversation renamed successfully');
                await refreshConversationHistory();
                setRenamingConversationId(null);
                setRenameValue('');
            } else {
                console.error('❌ Failed to rename conversation:', result.error);
                alert(`Failed to rename conversation: ${result.error}`);
            }
        } catch (error) {
            console.error('❌ Error renaming conversation:', error);
            alert('An error occurred while renaming the conversation');
        }
    };
    
    const handleDeleteConversation = async (conversationId, conversationTitle) => {
        if (window.confirm(`Are you sure you want to delete "${conversationTitle}"? This action cannot be undone.`)) {
            try {
                console.log('🗑️ Deleting conversation:', conversationId);
                const result = await deleteConversation(conversationId);
                
                if (result.success) {
                    console.log('✅ Conversation deleted successfully');
                    await refreshConversationHistory();
                } else {
                    console.error('❌ Failed to delete conversation:', result.error);
                    alert(`Failed to delete conversation: ${result.error}`);
                }
            } catch (error) {
                console.error('❌ Error deleting conversation:', error);
                alert('An error occurred while deleting the conversation');
            }
        }
    };
    
    const startRename = (conversationId, currentTitle) => {
        setRenamingConversationId(conversationId);
        setRenameValue(currentTitle);
    };
    
    const cancelRename = () => {
        setRenamingConversationId(null);
        setRenameValue('');
    };
    
    // All useEffect hooks
    // Watch for changes in selectedArtist localStorage
    useEffect(() => {
        const checkSelectedArtist = () => {
            const currentSelectedArtist = localStorage.getItem('selectedArtist');
            if (currentSelectedArtist !== selectedArtist) {
                setSelectedArtist(currentSelectedArtist);
            }
        };
        
        // Check immediately
        checkSelectedArtist();
        
        // Set up interval to check for changes
        const interval = setInterval(checkSelectedArtist, 1000);
        
        // Also listen for storage events (though they don't always fire for same-origin changes)
        window.addEventListener('storage', checkSelectedArtist);
        
        // Listen for new conversation events
        const handleNewConversation = (event) => {
            console.log('🆕 New conversation event received:', event.detail);
            console.log('📊 Current conversation state before refresh:', {
                count: conversationHistory.length,
                titles: conversationHistory.map(c => c.title)
            });
            setTimeout(() => {
                console.log('⏰ About to refresh conversation history after new conversation...');
                refreshConversationHistory();
            }, 1000); // Small delay to ensure backend is updated
        };
        
        const handleConversationUpdated = (event) => {
            console.log('🔄 Conversation updated event received:', event.detail);
            console.log('📊 Current conversation state before refresh:', {
                count: conversationHistory.length,
                titles: conversationHistory.map(c => c.title)
            });
            setTimeout(() => {
                console.log('⏰ About to refresh conversation history after update...');
                refreshConversationHistory();
            }, 500);
        };
        
        window.addEventListener('newConversationCreated', handleNewConversation);
        window.addEventListener('conversationUpdated', handleConversationUpdated);
        
        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', checkSelectedArtist);
            window.removeEventListener('newConversationCreated', handleNewConversation);
            window.removeEventListener('conversationUpdated', handleConversationUpdated);
        };
    }, [selectedArtist]);

    useEffect(() => {
        let musicPage = focusData?.music || []
        let productPage = focusData?.product || []
        let eventPage = focusData?.event || []
        let newsletterPage = focusData?.newsletter || []
        let customPage = focusData?.custom || []
        setFocusPageList([...musicPage, ...productPage, ...eventPage, ...newsletterPage, customPage])
    }, [focusData])

    useEffect(() => {
        (async () => {
            try {
                console.log('🔄 Loading conversation history for selectedArtist:', selectedArtist);
                console.log('🔑 Token available:', !!localStorage.getItem('dizeeToken'));
                
                setIsConversationHistoryLoading(true);
                let { success, conversations, error } = await getConversationHistoryForDizee();
                
                console.log('📊 Conversation history API response:', { success, conversations, error });
                
                if (success) {
                    console.log('✅ Successfully loaded conversations:', conversations?.length || 0, 'items');
                    setConversationHistory(conversations || []);
                    
                    // Auto-expand conversation history if we have conversations
                    if (conversations && conversations.length > 0) {
                        console.log('🔓 Auto-expanding conversation history section');
                        setIsConversationHistoryExpanded(true);
                    }
                } else {
                    console.error('❌ Failed to load conversations:', error);
                    setConversationHistory([]);
                }
                
                console.log(`conversations for context (selectedArtist: ${selectedArtist}):`, conversations);
            } catch (error) {
                console.error('💥 Exception loading conversation history:', error);
                setConversationHistory([]);
            } finally {
                setIsConversationHistoryLoading(false);
            }
        })()
    }, [selectedArtist])

    // Debug: Track conversation history changes
    useEffect(() => {
        console.log('🔄 conversationHistory state changed:', {
            count: conversationHistory.length,
            conversations: conversationHistory.map(c => ({
                id: c._id,
                title: c.title,
                createdAt: c.createdAt
            })),
            timestamp: new Date().toISOString()
        });
    }, [conversationHistory]);

    // Load custom prompts
    useEffect(() => {
        const loadCustomPrompts = async () => {
            try {
                const token = JSON.parse(localStorage.getItem('dizeeToken'));
                const response = await promptsApi.getAllPrompts(token);
                
                if (response.success) {
                    setCustomPrompts(response.prompts || []);
                } else {
                    console.error('Failed to load custom prompts:', response.message);
                }
            } catch (error) {
                console.error('Error loading custom prompts:', error);
            }
        };

        loadCustomPrompts();
    }, [selectedArtist]);

    // Load accessibility settings from localStorage on mount
    useEffect(() => {
        const savedSettings = localStorage.getItem('dizee-accessibility-settings');
        if (savedSettings) {
            try {
                const parsed = JSON.parse(savedSettings);
                setAccessibilitySettings(parsed);
                applyAccessibilitySettings(parsed);
            } catch (e) {
                console.error('Failed to parse accessibility settings:', e);
            }
        }
        
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (mediaQuery.matches) {
            const updatedSettings = { ...accessibilitySettings, reduceMotion: true };
            setAccessibilitySettings(updatedSettings);
            applyAccessibilitySettings(updatedSettings);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effect to update reminders count
    useEffect(() => {
        const updateRemindersCount = () => {
            try {
                const { getUnviewedRemindersCount, getActiveRemindersCount } = require('../utils/remindersData');
                const unviewedCount = getUnviewedRemindersCount();
                const activeCount = getActiveRemindersCount();
                
                setUnviewedRemindersCount(unviewedCount);
                setActiveRemindersCount(activeCount);
            } catch (error) {
                console.error('Error getting reminders count:', error);
            }
        };

        // Update immediately
        updateRemindersCount();

        // Update every 30 seconds (faster since it's localStorage based)
        const interval = setInterval(updateRemindersCount, 30000);

        return () => clearInterval(interval);
    }, [selectedArtist]);

    // Native event listener for complete scroll blocking when everything is collapsed
    // MOBILE FIX: Only prevent touchmove (scrolling) and wheel events, not touchstart (tapping)
    // This ensures taps work properly on mobile while preventing unwanted scrolling
    useEffect(() => {
        const handleWheel = (e) => {
            const allCollapsed = !isConversationHistoryExpanded && 
                               !isCustomPromptsExpanded && 
                               !isAppsExpanded;
            
            if (allCollapsed) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };

        const handleTouchMove = (e) => {
            const allCollapsed = !isConversationHistoryExpanded && 
                               !isCustomPromptsExpanded && 
                               !isAppsExpanded;
            
            if (allCollapsed) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };

        // Remove touchstart prevention to allow taps/clicks to work on mobile
        // Only prevent scrolling (touchmove) and wheel events, not initial touch (touchstart)

        const sidebar = sidebarRef.current;
        if (sidebar) {
            sidebar.addEventListener('wheel', handleWheel, { passive: false, capture: true });
            sidebar.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
            // Removed touchstart event listener to allow taps to work
            return () => {
                sidebar.removeEventListener('wheel', handleWheel, { capture: true });
                sidebar.removeEventListener('touchmove', handleTouchMove, { capture: true });
                // No touchstart removal needed since we're not adding it
            };
        }
    }, [drawerVisible, isConversationHistoryExpanded, isCustomPromptsExpanded, isAppsExpanded]);

    // Cleanup body scroll lock and sidebar class when component unmounts
    useEffect(() => {
        return () => {
            // Restore body scroll on cleanup
            document.body.style.overflow = 'unset';
            document.body.style.position = 'static';
            document.body.style.width = 'auto';
            document.body.style.height = 'auto';
            document.body.style.touchAction = 'auto';
            document.documentElement.style.overflow = 'unset';
            document.body.classList.remove('sidebar-open');
        };
    }, [])

    // Prevent background scroll with event listeners
    // MOBILE FIX: Enhanced to allow taps while preventing scrolling
    useEffect(() => {
        const preventScroll = (e) => {
            // Allow scrolling within the sidebar
            const sidebarElement = e.target.closest('[data-sidebar]');
            if (sidebarElement) {
                return; // Allow scroll within sidebar
            }
            
            // For touch events, only prevent if it's actually a scroll gesture
            if (e.type === 'touchmove') {
                e.preventDefault();
            } else if (e.type === 'wheel') {
                e.preventDefault();
            }
            // Don't prevent touchstart events to allow taps
        };

        if (drawerVisible) {
            // Prevent scroll on mobile - sidebar open
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.height = '100%';
            document.body.style.touchAction = 'none'; // Prevent touch gestures on body
            document.documentElement.style.overflow = 'hidden';
            document.body.classList.add('sidebar-open');

            // Ensure sidebar itself allows touch interactions
            const sidebar = sidebarRef.current;
            if (sidebar) {
                sidebar.style.touchAction = 'auto'; // Allow touch interactions within sidebar
            }

            // Add touch event listeners to prevent scrolling (but not tapping)
            document.addEventListener('touchmove', preventScroll, { passive: false });
            document.addEventListener('wheel', preventScroll, { passive: false });
            // Don't add touchstart listener to allow taps to work
        } else {
            // Restore scroll
            document.body.classList.remove('sidebar-open');
            document.body.style.overflow = 'unset';
            document.body.style.position = 'static';
            document.body.style.width = 'auto';
            document.body.style.height = 'auto';
            document.body.style.touchAction = 'auto';
            document.documentElement.style.overflow = 'unset';

            // Restore sidebar touch-action
            const sidebar = sidebarRef.current;
            if (sidebar) {
                sidebar.style.touchAction = 'auto';
            }

            // Remove event listeners
            document.removeEventListener('touchmove', preventScroll);
            document.removeEventListener('wheel', preventScroll);
        }

        return () => {
            // Cleanup
            document.body.style.overflow = 'unset';
            document.body.style.position = 'static';
            document.body.style.width = 'auto';
            document.body.style.height = 'auto';
            document.body.style.touchAction = 'auto';
            document.documentElement.style.overflow = 'unset';
            document.body.classList.remove('sidebar-open');
            document.removeEventListener('touchmove', preventScroll);
            document.removeEventListener('wheel', preventScroll);
        };
    }, [drawerVisible]);

    // Check if we're on team member dashboard
    const isTeamMemberDashboard = location.pathname === '/team-member-dashboard' || 
                                 location.pathname === '/manager-dashboard' ||
                                 location.pathname === '/team-member/profile' ||
                                 location.pathname === '/team-member/subscription' ||
                                 location.pathname === '/team-member/advanced' ||
                                 (location.pathname === '/' && user && ['manager', 'agent', 'label', 'publisher', 'business-manager'].includes(user.professionRole) && !localStorage.getItem('selectedArtist'));
    
    const toggleDrawer = () => {
        const newDrawerState = !drawerVisible;
        setDrawerVisible(newDrawerState);
        
        // Debug logging when sidebar is opened
        if (newDrawerState) {
            console.log('🔍 Sidebar opened - Current state:', {
                conversationHistoryLength: conversationHistory.length,
                isConversationHistoryExpanded,
                isConversationHistoryLoading,
                conversationTitles: conversationHistory.map(c => c.title),
                conversationIds: conversationHistory.map(c => c._id),
                conversationDates: conversationHistory.map(c => c.createdAt),
                filteredConversationsLength: conversationHistory.filter(conversation => {
                    if (!conversation || !conversation.title) return false;
                    return conversation.title.toLowerCase().includes(searchQuery.toLowerCase());
                }).length,
                searchQuery
            });
        }
        
        if (newDrawerState) {
            document.body.classList.add('sidebar-open');
        } else {
            document.body.classList.remove('sidebar-open');
        }
    };

    const getProfileImage = () => {
        if (userArticle?.avatar) {
            return removeUploadsFromUrl(`${userArticle.avatar}`);
        } else if (user?.avatar) {
            return user.avatar.includes('/uploads/')
                ? `${BACKEND_URL}${removeUploadsFromUrl(user.avatar)}`
                : user.avatar;
        }
        return 'https://placehold.co/40x40';
    };

    const handleLogout = () => {
        dispatch(clearAlerts());
        dispatch(logoutThunk());
    };

    // AI Mode handlers
    const handleAIModeToggle = () => {
        if (pendingAIModeChange !== null) {
            const newValue = pendingAIModeChange;
            setAiModeEnabled(newValue);
            localStorage.setItem('dizeeAIModeEnabled', JSON.stringify(newValue));
            
            // Dispatch event so other components can react to AI mode change
            window.dispatchEvent(new CustomEvent('aiModeChanged', { detail: { enabled: newValue } }));
            
            setPendingAIModeChange(null);
            setShowAIModeModal(false);
        }
    };

    // Accessibility functions
    const applyAccessibilitySettings = (newSettings) => {
        const root = document.documentElement;
        const body = document.body;
        
        const fontSizeMap = {
            small: '14px',
            medium: '16px',
            large: '18px',
            xlarge: '20px',
        };
        
        const scaleMap = {
            small: '0.875',
            medium: '1',
            large: '1.125',
            xlarge: '1.25',
        };
        
        root.style.setProperty('--base-font-size', fontSizeMap[newSettings.fontSize]);
        root.style.fontSize = fontSizeMap[newSettings.fontSize];
        
        // Apply zoom to body for site-wide scaling
        body.style.zoom = scaleMap[newSettings.fontSize];

        if (newSettings.highContrast) {
            root.setAttribute('data-high-contrast', 'true');
        } else {
            root.removeAttribute('data-high-contrast');
        }

        if (newSettings.dyslexicFont) {
            // Apply local OpenDyslexic font globally with maximum specificity for mobile and desktop
            const fontStyle = document.getElementById('dyslexic-font-style') || document.createElement('style');
            fontStyle.id = 'dyslexic-font-style';
            fontStyle.textContent = `
                /* Base rule for all elements */
                * {
                    font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
                }
                
                /* Target pseudo-elements */
                *::before, *::after {
                    font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
                }
                
                /* Override styled-components and inline styles */
                [style*="font-family"] {
                    font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
                }
                
                /* Target common structural elements explicitly */
                html, body, div, span, p, a, button, input, textarea, select, label,
                h1, h2, h3, h4, h5, h6, ul, ol, li, table, td, th, tr, nav, section, article, aside, header, footer {
                    font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
                }
            `;
            document.head.appendChild(fontStyle);
        } else {
            // Remove dyslexic font style
            const fontStyle = document.getElementById('dyslexic-font-style');
            if (fontStyle) fontStyle.remove();
            root.style.fontFamily = '';
        }

        // THEME: Use filters (perfect color inversion) + aggressive overflow protection
        
        if (newSettings.theme === 'light') {
            root.setAttribute('data-theme', 'light');
            /**
             * Light mode is implemented via full-page inversion.
             * Important: the *pre-inversion* base must remain dark, otherwise
             * setting white + invert yields a dark UI (and breaks contrast).
             */
            // Ensure the entire viewport (including html background) is inverted
            root.style.backgroundColor = '#000000';
            body.style.backgroundColor = '#000000';
            body.style.color = '#ffffff';
            body.style.filter = 'none';
            
            // Apply filter for full color inversion on the root element
            root.style.filter = 'invert(1) hue-rotate(180deg)';
            
            // AGGRESSIVE overflow protection - inject CSS that persists
            const style = document.getElementById('theme-scroll-fix') || document.createElement('style');
            style.id = 'theme-scroll-fix';
            style.textContent = `
                /* Re-invert images so they appear normal */
                img:not([alt*="DIZEE"]), video, canvas {
                    filter: invert(1) hue-rotate(180deg) !important;
                }
                
                /* CRITICAL: Body/html should NEVER scroll - only mobile viewport scrolls */
                /* This matches dark mode behavior perfectly */
                html, html[data-theme="light"], html[data-theme="grayscale"] {
                    overflow: hidden !important;
                    overflow-x: hidden !important;
                    overflow-y: hidden !important;
                    position: relative !important;
                    height: 100% !important;
                }
                
                body, body[data-theme="light"], body[data-theme="grayscale"] {
                    overflow: hidden !important;
                    overflow-x: hidden !important;
                    overflow-y: hidden !important;
                    position: relative !important;
                    height: 100% !important;
                }
                
                #root {
                    overflow: visible !important;
                    height: 100% !important;
                }
            `;
            document.head.appendChild(style);
            
        } else if (newSettings.theme === 'grayscale') {
            root.setAttribute('data-theme', 'grayscale');
            body.style.backgroundColor = '#000000';
            body.style.color = '#ffffff';
            body.style.filter = 'none';
            
            // Apply grayscale filter on the root element (covers full viewport)
            root.style.filter = 'grayscale(100%)';
            
            // Same aggressive overflow protection
            const style = document.getElementById('theme-scroll-fix') || document.createElement('style');
            style.id = 'theme-scroll-fix';
            style.textContent = `
                /* Body/html should NEVER scroll - only mobile viewport scrolls */
                html, html[data-theme="grayscale"] {
                    overflow: hidden !important;
                    overflow-x: hidden !important;
                    overflow-y: hidden !important;
                    position: relative !important;
                    height: 100% !important;
                }
                
                body, body[data-theme="grayscale"] {
                    overflow: hidden !important;
                    overflow-x: hidden !important;
                    overflow-y: hidden !important;
                    position: relative !important;
                    height: 100% !important;
                }
                
                #root {
                    overflow: visible !important;
                    height: 100% !important;
                }
            `;
            document.head.appendChild(style);
            
        } else {
            root.setAttribute('data-theme', 'dark');
            body.style.backgroundColor = '#000000';
            body.style.color = '#ffffff';
            body.style.filter = 'none';
            root.style.filter = 'none';
            
            // Apply same overflow protection for dark mode (consistency across all themes)
            const style = document.getElementById('theme-scroll-fix') || document.createElement('style');
            style.id = 'theme-scroll-fix';
            style.textContent = `
                /* Body/html should NEVER scroll - only mobile viewport scrolls */
                html, html[data-theme="dark"] {
                    overflow: hidden !important;
                    overflow-x: hidden !important;
                    overflow-y: hidden !important;
                    position: relative !important;
                    height: 100% !important;
                }
                
                body, body[data-theme="dark"] {
                    overflow: hidden !important;
                    overflow-x: hidden !important;
                    overflow-y: hidden !important;
                    position: relative !important;
                    height: 100% !important;
                }
                
                #root {
                    overflow: visible !important;
                    height: 100% !important;
                }
            `;
            document.head.appendChild(style);
        }

        if (newSettings.reduceMotion) {
            root.style.setProperty('--animation-duration', '0.01ms');
            root.style.setProperty('--transition-duration', '0.01ms');
            root.setAttribute('data-reduce-motion', 'true');
            
            // Add comprehensive motion reduction
            const motionStyle = document.getElementById('reduce-motion-style') || document.createElement('style');
            motionStyle.id = 'reduce-motion-style';
            motionStyle.textContent = `
                /* Disable all animations and transitions */
                *, *::before, *::after {
                    animation-duration: 0.01ms !important;
                    animation-iteration-count: 1 !important;
                    transition-duration: 0.01ms !important;
                    scroll-behavior: auto !important;
                }
                
                /* Disable auto-playing videos */
                video {
                    animation-play-state: paused !important;
                }
                
                /* Disable parallax effects */
                [style*="transform"] {
                    transform: none !important;
                }
                
                /* Stop GIF animations */
                img[src*=".gif"] {
                    animation: none !important;
                }
            `;
            document.head.appendChild(motionStyle);
        } else {
            root.style.removeProperty('--animation-duration');
            root.style.removeProperty('--transition-duration');
            root.removeAttribute('data-reduce-motion');
            
            // Remove motion reduction style
            const motionStyle = document.getElementById('reduce-motion-style');
            if (motionStyle) motionStyle.remove();
        }
    };

    const updateAccessibilitySetting = (key, value) => {
        const newSettings = { ...accessibilitySettings, [key]: value };
        setAccessibilitySettings(newSettings);
        applyAccessibilitySettings(newSettings);
        localStorage.setItem('dizee-accessibility-settings', JSON.stringify(newSettings));
    };

    const resetAccessibilitySettings = () => {
        const defaultSettings = {
            fontSize: 'medium',
            highContrast: false,
            dyslexicFont: false,
            theme: 'dark',
            reduceMotion: false,
        };
        setAccessibilitySettings(defaultSettings);
        applyAccessibilitySettings(defaultSettings);
        localStorage.removeItem('dizee-accessibility-settings');
        document.body.style.zoom = '1';
    };

    const navigateItem = (item) => {
        let type = item.id.split('-')[0];
        let subType = item.type;
        switch (type) {
            case 'music': {

                dispatch(setWholeFocusMusic(item));
                if (subType == 'single') {
                    navigate('/focus-page/music-page');
                } else {
                    navigate('/focus-page/multiple-music-page');
                }

                break;
            }
            case 'product': {
                dispatch(setWholeFocusProduct(item));
                if (subType == 'single') {
                    navigate('/focus-page/product-page');
                } else {
                    navigate('/focus-page/multiple-product-page');
                }
                break;
            }
            case 'event': {
                dispatch(setWholeFocusEvent(item));
                if (subType == 'single') {
                    navigate('/focus-page/event-page');
                } else {
                    navigate('/focus-page/multiple-event-page');
                }
                break;
            }
            case 'newsletter': {
                dispatch(setWholeFocusNewsletter(item));
                if (subType == 'single') {
                    navigate('/focus-page/newsletter-page');
                } else {
                    navigate('/focus-page/multiple-newsletter-page');
                }
                break;
            }
            case 'custom': {
                dispatch(setWholeFocusCustom(item));
                navigate('/focus-page/custom-page');
                break;
            }
        }
    }

    const handleClickConversation = async (conversation) => {
        // Close the drawer first
        setDrawerVisible(false);
        
        // CRITICAL: Remove sidebar-open class to ensure FooterBar isn't hidden by CSS
        document.body.classList.remove('sidebar-open');
        
        // Restore body styles
        document.body.style.overflow = 'unset';
        document.body.style.touchAction = 'auto';
        document.documentElement.style.overflow = 'unset';
        
        try {
            // If not on homepage, navigate there first
            if (!isHome) {
                console.log('📍 Not on homepage, navigating to landing page first');
                navigate('/');
                
                // Wait a moment for navigation to complete
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            // Switch to chat tab and maximize on landing page
            // This event will be picked up by the landing page
            window.dispatchEvent(new CustomEvent('switchToChatTab', {
                detail: { maximize: true }
            }));
            
            // Load conversation immediately without closing chat first
            await dispatch(setChatHistoryThunk({
                conversationId: conversation._id,
                token: token
            }));
            
            // Open chat immediately
            dispatch(openChat());
            
            // Notify FooterBar immediately
            window.dispatchEvent(new CustomEvent('conversationLoaded', { 
                detail: { conversationId: conversation._id } 
            }));
            
            console.log('✅ Conversation loaded and navigated to chat view');
            
        } catch (error) {
            console.error('❌ Error loading conversation:', error);
            // Fallback to opening chat anyway
            dispatch(openChat());
        }
    }

    const togglePromptEnabled = async (promptId, currentEnabled) => {
        try {
            // Optimistically update UI
            setCustomPrompts(prev => prev.map(prompt => 
                prompt._id === promptId 
                    ? { ...prompt, enabled: !currentEnabled }
                    : prompt
            ));

            // Update on server
            await promptsApi.togglePromptEnabled(promptId, !currentEnabled);
        } catch (error) {
            console.error('Failed to toggle prompt:', error);
            // Revert UI on error
            setCustomPrompts(prev => prev.map(prompt => 
                prompt._id === promptId 
                    ? { ...prompt, enabled: currentEnabled }
                    : prompt
            ));
        }
    };

    const handleFeedbackSubmit = async () => {
        if (!feedback.trim()) {
            dispatch(setErrorAlert('Please enter your feedback'));
            return;
        }

        if (!navigator.onLine) {
            dispatch(setErrorAlert('Please check your internet connection'));
            return;
        }
        
        try {
            setFeedbackSubmitting(true);
            const result = await dispatch(submitFeedbackThunk({ 
                feedback: feedback.trim(), 
                page: location.pathname 
            })).unwrap();
            
            if (result?.status === 200) {
                dispatch(setSuccessAlert(result.message || 'Feedback submitted successfully'));
                setFeedbackSubmitted(true);
                setTimeout(() => {
                    setFeedbackOpen(false);
                    setFeedbackSubmitted(false);
                    setFeedback('');
                }, 2000);
            }
        } catch (error) {
            console.error('Feedback submission error:', error);
        } finally {
            setFeedbackSubmitting(false);
        }
    };

    // Filter conversations based on search query
    const filteredConversations = conversationHistory.filter(conversation => {
        if (!conversation || !conversation.title) {
            console.warn('⚠️ Invalid conversation object:', conversation);
            return false;
        }
        return conversation.title.toLowerCase().includes(searchQuery.toLowerCase());
    });

    // Comprehensive logging for conversation pipeline
    console.log('📋 Conversation Pipeline Debug:', {
        // Raw data
        totalConversationsLoaded: conversationHistory.length,
        conversationsRaw: conversationHistory,
        
        // Filtering
        searchQuery: searchQuery,
        filteredCount: filteredConversations.length,
        filteredConversations: filteredConversations,
        
        // UI State
        isConversationHistoryExpanded: isConversationHistoryExpanded,
        isConversationHistoryLoading: isConversationHistoryLoading,
        sidebarVisible: drawerVisible,
        
        // Rendering logic
        conversationsToRender: [...filteredConversations],
        renderCount: filteredConversations.length,
        
        // Data structure validation
        conversationStructure: conversationHistory.map(conv => ({
            hasTitle: !!conv?.title,
            hasId: !!conv?._id,
            hasCreatedAt: !!conv?.createdAt,
            title: conv?.title,
            id: conv?._id,
            createdAt: conv?.createdAt
        })),
        
        // React re-render tracking
        timestamp: new Date().toISOString(),
        componentRenderCount: Math.random() // Unique ID for this render
    });

    return (
        <div className="pt-[10%] w-full flex flex-row items-center justify-between relative">
            {!isTeamMemberDashboard && (
            <motion.div 
                className="w-[70px] h-[40px] flex items-center cursor-pointer relative" 
                onClick={toggleDrawer}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
                <motion.div
                    animate={{ rotate: drawerVisible ? 90 : 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                <Line2 />
                </motion.div>
                {/* Red dot notification for active reminders */}
                {activeRemindersCount > 0 && (
                    <div className="absolute top-[5px] right-[15px] w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-[8px] font-bold">
                            {activeRemindersCount > 9 ? '9+' : activeRemindersCount}
                        </span>
                    </div>
                )}
            </motion.div>
            )}
            
            {/* Simple delegated access indicator */}
            {localStorage.getItem('selectedArtist') && (
                <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    padding: '6px 16px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    minWidth: 'max-content',
                    maxWidth: '300px'
                }} onClick={returnToTeamHub}>
                    {(() => {
                        try {
                            const artist = JSON.parse(localStorage.getItem('selectedArtist'));
                            return `Acting as ${artist.name} • Return to Team Hub`;
                        } catch {
                            return 'Return to Team Hub';
                        }
                    })()}
                </div>
            )}
            
            {!isTeamMemberDashboard && !isHome && (
                <div className="h-[40px] flex items-center">
                    <span onClick={() => navigate('/')} className="cursor-pointer text-white text-[12px] font-normal">HOME</span>
                </div>
            )}
            
            {/* SIMPLIFY/EXPAND toggle - Only shown when on home page with apps tab active */}
            {isHome && activeTab === 'apps' && onAppsViewToggle && (
                <div className="h-[40px] flex items-center">
                    <motion.button
                        onClick={onAppsViewToggle}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className="group relative cursor-pointer text-white/90 transition-colors p-0 bg-transparent border-0 focus:outline-none"
                        title={isAppsViewExpanded ? 'Simplify view' : 'Expand view'}
                    >
                        <span
                            className="relative w-[22px] h-[22px] flex items-center justify-center"
                            style={{
                                transition: 'filter 420ms cubic-bezier(0.16, 1, 0.3, 1)',
                                filter: 'drop-shadow(0 0 0 rgba(0,0,0,0))'
                            }}
                        >
                            <AnimatePresence mode="wait" initial={false}>
                                {isAppsViewExpanded ? (
                                    <motion.span
                                        key="compact"
                                        initial={{ opacity: 0, rotate: -12, y: 1 }}
                                        animate={{ opacity: 1, rotate: 0, y: 0 }}
                                        exit={{ opacity: 0, rotate: 12, y: -1 }}
                                        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                                        className="block group-hover:[filter:drop-shadow(0_0_10px_rgba(255,35,0,0.28))] group-focus-visible:[filter:drop-shadow(0_0_10px_rgba(255,35,0,0.28))]"
                                    >
                                        {/* Grid icon - 4 small squares (simplify/compact) */}
                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.9"/>
                                            <rect x="11" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.9"/>
                                            <rect x="1" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.9"/>
                                            <rect x="11" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.9"/>
                                        </svg>
                                    </motion.span>
                                ) : (
                                    <motion.span
                                        key="expand"
                                        initial={{ opacity: 0, rotate: 12, y: 1 }}
                                        animate={{ opacity: 1, rotate: 0, y: 0 }}
                                        exit={{ opacity: 0, rotate: -12, y: -1 }}
                                        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                                        className="block group-hover:[filter:drop-shadow(0_0_10px_rgba(255,35,0,0.28))] group-focus-visible:[filter:drop-shadow(0_0_10px_rgba(255,35,0,0.28))]"
                                    >
                                        {/* List/rows icon (expand/detailed) */}
                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="1" y="1" width="16" height="4" rx="1" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.9"/>
                                            <rect x="1" y="7" width="16" height="4" rx="1" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.9"/>
                                            <rect x="1" y="13" width="16" height="4" rx="1" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.9"/>
                                        </svg>
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </span>
                    </motion.button>
                </div>
            )}
            <AnimatePresence mode="wait">
                {drawerVisible && (
                    <>
                        {/* Enhanced Backdrop with blur and fade */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[10001]"
                        onClick={toggleDrawer}
                    />

                        {/* Enhanced Drawer with material motion */}
                        <motion.div
                            ref={sidebarRef}
                            data-sidebar
                            data-sidebar-drawer="true"
                            initial={{ 
                                x: -320,
                                opacity: 0,
                                scale: 0.95
                            }}
                            animate={{ 
                                x: 0,
                                opacity: 1,
                                scale: 1
                            }}
                            exit={{ 
                                x: -320,
                                opacity: 0,
                                scale: 0.95
                            }}
                            transition={{
                                type: "spring",
                                damping: 25,
                                stiffness: 200,
                                mass: 1.2,
                                duration: 0.5
                            }}
                            className="fixed top-0 bottom-0 w-[300px] bg-black z-[10002] border-r border-[#1A1A1A] overflow-hidden shadow-2xl"
                            style={{ 
                                left: 'calc(50% - 195px)',
                                overscrollBehavior: 'contain',
                                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.05)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <motion.div 
                                initial="hidden"
                                animate="visible"
                                variants={{
                                    hidden: { opacity: 0 },
                                    visible: {
                                        opacity: 1,
                                        transition: {
                                            staggerChildren: 0.1,
                                            delayChildren: 0.2
                                        }
                                    }
                                }}
                                className="text-white flex flex-col h-full"
                            >
                                {/* Logo Section - At the very top */}
                                <motion.div 
                                    variants={{
                                        hidden: { opacity: 0, y: -20 },
                                        visible: { 
                                            opacity: 1, 
                                            y: 0,
                                            transition: { duration: 0.5, ease: "easeOut" }
                                        }
                                    }}
                                    className="flex-shrink-0 px-4 py-6 flex justify-start border-b border-[#1A1A1A]"
                                >
                                    <motion.div
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                                    >
                                    <LogoImage src={dizeeLogo} alt="DIZEE" />
                                    </motion.div>
                                </motion.div>
                                
                                {/* Usage Bar - Positioned below logo */}
                                <motion.div 
                                    variants={{
                                        hidden: { opacity: 0, y: -15 },
                                        visible: { 
                                            opacity: 1, 
                                            y: 0,
                                            transition: { duration: 0.4, ease: "easeOut" }
                                        }
                                    }}
                                    className="flex-shrink-0"
                                >
                                    <UsageBar />
                                </motion.div>
                                
                                {/* Header Section - Search moved to conversation history */}
                                
                                {/* Main Content - Scrollable within sidebar only */}
                                <div 
                                    className="flex-1 overflow-y-auto pb-4" 
                                    style={{
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: '#2A2A2A #111111',
                                        overscrollBehavior: 'contain'
                                    }}
                                >

                                    {/* Conversation History Section */}
                                    <div className="p-4 border-b border-[#1A1A1A]">
                                        <div className="flex flex-row items-center justify-between w-full mb-4">
                                            <motion.div 
                                                className="flex flex-row items-center gap-x-[15px] cursor-pointer"
                                                onClick={() => setIsConversationHistoryExpanded(!isConversationHistoryExpanded)}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                <span className="text-[12px] text-white">
                                                    Conversation History 
                                                    {conversationHistory.length > 0 && (
                                                        <span className="ml-1 text-[10px] text-[#8E8E93]">
                                                            ({conversationHistory.length})
                                                        </span>
                                                    )}
                                                </span>
                                                <motion.div
                                                    animate={{ rotate: isConversationHistoryExpanded ? 90 : 0 }}
                                                    transition={{ duration: 0.2, ease: "easeInOut" }}
                                                    className="ml-1"
                                                >
                                                    <svg className="w-3 h-3 text-[#8E8E93]" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </motion.div>
                                            </motion.div>
                                            <div className="flex gap-2">
                                                <motion.button 
                                                    onClick={() => { 
                                                        console.log('🆕 New Chat button clicked in sidebar');
                                                        // Dispatch event to FooterBar to properly clear all state
                                                        window.dispatchEvent(new CustomEvent('newChatRequested'));
                                                        setDrawerVisible(false); 
                                                    }}
                                                    whileHover={{ 
                                                        backgroundColor: "#007AFF",
                                                        color: "#FFFFFF"
                                                    }}
                                                    whileTap={{ scale: 0.98 }}
                                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                    className="text-[#007AFF] text-[10px] px-2 py-1 rounded border border-[#007AFF] hover:bg-[#007AFF] hover:text-white transition-colors"
                                                >
                                                    New Chat
                                                </motion.button>
                                            </div>
                                        </div>

                                        <AnimatePresence initial={false}>
                                            {isConversationHistoryExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ 
                                                        duration: 0.3, 
                                                        ease: "easeInOut",
                                                        opacity: { duration: 0.2 }
                                                    }}
                                                    style={{ overflow: "hidden" }}
                                                >
                                                    {/* Search Input - Now inside conversation history */}
                                                    <div className="mb-4">
                                                        <motion.input 
                                                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                            type="text" 
                                                            value={searchQuery}
                                                            onChange={(e) => setSearchQuery(e.target.value)}
                                                            className='w-full h-[32px] bg-[#1A1A1A] rounded-[6px] outline-none placeholder:text-[#8E8E93] px-[12px] text-[11px] border border-[#2A2A2A] focus:border-[#FF4500] transition-colors' 
                                                            placeholder='Search conversations...' 
                                                        />
                                                    </div>
                                        <div 
                                            className="flex flex-col gap-y-[5px] max-h-[200px] overflow-y-auto" 
                                            style={{
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none',
                                                overscrollBehavior: 'contain'
                                            }}
                                        >
                                            <style>
                                                {`.conversation-history::-webkit-scrollbar { display: none; }`}
                                            </style>
                                            {isConversationHistoryLoading ? (
                                                <div className="text-[12px] text-[#8E8E93] text-center py-4">
                                                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-[#8E8E93]"></div>
                                                    <span className="ml-2">Loading conversations...</span>
                                                </div>
                                            ) : (
                                                [...filteredConversations].map((conversation, key) => {
                                                    console.log(`🎨 ACTUALLY RENDERING conversation ${key}:`, {
                                                        conversation,
                                                        hasTitle: !!conversation?.title,
                                                        hasId: !!conversation?._id,
                                                        hasCreatedAt: !!conversation?.createdAt,
                                                        title: conversation?.title,
                                                        createdAt: conversation?.createdAt,
                                                        willShowInUI: true,
                                                        renderTimestamp: Date.now()
                                                    });
                                                    
                                                    return (
                                                        <motion.div 
                                                            key={conversation?._id || key} 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ 
                                                                delay: key * 0.1,
                                                                duration: 0.3,
                                                                ease: "easeOut"
                                                            }}
                                                            whileHover={{ 
                                                                backgroundColor: "#1A1A1A",
                                                                transition: { duration: 0.2 }
                                                            }}
                                                            className="group p-2 rounded-md cursor-pointer"
                                                            onClick={() => handleClickConversation(conversation)}
                                                        >
                                                            <div className="flex flex-col gap-1">
                                                                {/* Title Row */}
                                                                <div>
                                                                    {renamingConversationId === conversation?._id ? (
                                                                        <input
                                                                            type="text"
                                                                            value={renameValue}
                                                                            onChange={(e) => setRenameValue(e.target.value)}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    handleRenameConversation(conversation._id, renameValue);
                                                                                } else if (e.key === 'Escape') {
                                                                                    cancelRename();
                                                                                }
                                                                            }}
                                                                            onBlur={() => handleRenameConversation(conversation._id, renameValue)}
                                                                            className="w-full text-[12px] text-white bg-[#2A2A2A] rounded px-2 py-1 outline-none border border-[#007AFF] focus:border-[#007AFF]"
                                                                            autoFocus
                                                                        />
                                                                    ) : (
                                                                                                                                           <div className="text-[12px] text-white truncate">
                                                                       {(() => {
                                                                           // Clean markers from conversation title
                                                                           const title = conversation?.title || 'Untitled Conversation';
                                                                           return title
                                                                               .replace(/^\[CATALOG_FORM:(release|demo)\]\s*/g, '')
                                                                               .replace(/^\[BOOKING_FORM:show\]\s*/g, '')
                                                                               .replace(/^\[CONFIRM_SHOWS:list\]\s*/g, '')
                                                                               .replace(/^\[TOURING_FLIGHTS:navigate\]\s*/g, '')
                                                                               .replace(/^\[TOURING_HOTELS:navigate\]\s*/g, '')
                                                                               .replace(/^\[TOURING_TRANSPORT:navigate\]\s*/g, '')
                                                                               .replace(/^\[ADD_TICKET_COUNTS:form\]\s*/g, '')
                                                                               .replace(/^\[TASK_FORM:[^\]]+\]\s*/g, '')
                                                                               .replace(/^\[NAVIGATE:[^\]]+\]\s*/g, '');
                                                                       })()}
                                                                   </div>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Date and Actions Row */}
                                                                <div className="flex items-center justify-between">
                                                                    <div className="text-[10px] text-[#8E8E93]">
                                                                        {conversation?.createdAt ? new Date(conversation.createdAt).toLocaleDateString() : 'No date'}
                                                                    </div>
                                                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                startRename(conversation._id, conversation.title);
                                                                            }}
                                                                            className="text-[10px] text-[#8E8E93] hover:text-white transition-colors"
                                                                            title="Rename conversation"
                                                                        >
                                                                            rename
                                                                        </button>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDeleteConversation(conversation._id, conversation.title);
                                                                            }}
                                                                            className="text-[10px] text-[#FF4500] hover:text-[#FF6500] transition-colors"
                                                                            title="Delete conversation"
                                                                        >
                                                                            delete
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    );
                                                })
                                            )}
                                            {!isConversationHistoryLoading && filteredConversations.length === 0 && (
                                                <div className="text-[12px] text-[#8E8E93] text-center py-4">
                                                    {searchQuery 
                                                        ? 'No conversations found' 
                                                        : conversationHistory.length === 0 
                                                            ? 'No conversations yet - start chatting to see history' 
                                                            : 'No conversations match your search'
                                                    }
                                                </div>
                                            )}
                                            {!isConversationHistoryLoading && filteredConversations.length > 0 && (
                                                <div className="text-[10px] text-[#8E8E93] text-center py-2 border-t border-[#1A1A1A]">
                                                    Showing all {filteredConversations.length} conversations
                                                </div>
                                            )}
                                        </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Custom Prompts Section - Temporarily hidden for MVP */}
                                    {/* <div className="p-4 border-b border-[#1A1A1A]">
                                        <div className="flex flex-row items-center justify-between w-full mb-4">
                                            <motion.div 
                                                className="flex flex-row items-center gap-x-[15px] cursor-pointer"
                                                onClick={() => setIsCustomPromptsExpanded(!isCustomPromptsExpanded)}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                            <span className="text-[12px] text-white">Custom Prompts</span>
                                                <motion.div
                                                    animate={{ rotate: isCustomPromptsExpanded ? 90 : 0 }}
                                                    transition={{ duration: 0.2, ease: "easeInOut" }}
                                                    className="ml-1"
                                                >
                                                    <svg className="w-3 h-3 text-[#8E8E93]" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </motion.div>
                                            </motion.div>
                                            <motion.button 
                                                onClick={() => { 
                                                    navigate('/prompts');
                                                    setDrawerVisible(false); 
                                                }}
                                                whileHover={{ 
                                                    backgroundColor: "#007AFF",
                                                    color: "#FFFFFF"
                                                }}
                                                whileTap={{ scale: 0.98 }}
                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                className="text-[#007AFF] text-[10px] px-2 py-1 rounded border border-[#007AFF]"
                                            >
                                                Manage
                                            </motion.button>
                                        </div>
                                        
                                        <AnimatePresence initial={false}>
                                            {isCustomPromptsExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ 
                                                        duration: 0.3, 
                                                        ease: "easeInOut",
                                                        opacity: { duration: 0.2 }
                                                    }}
                                                    style={{ overflow: "hidden" }}
                                                >
                                        {customPrompts.length === 0 ? (
                                            <div className="text-[12px] text-[#8E8E93] text-center py-4">
                                                No custom prompts yet
                                            </div>
                                        ) : (
                                            <div 
                                                className="flex flex-col gap-y-[8px] max-h-[150px] overflow-y-auto" 
                                                style={{
                                                    scrollbarWidth: 'none',
                                                    msOverflowStyle: 'none',
                                                    overscrollBehavior: 'contain'
                                                }}
                                            >
                                                            {customPrompts.map((prompt, index) => (
                                                                <motion.div 
                                                        key={prompt._id}
                                                                    initial={{ opacity: 0, x: -20 }}
                                                                    animate={{ opacity: 1, x: 0 }}
                                                                    transition={{ 
                                                                        delay: index * 0.1,
                                                                        duration: 0.3,
                                                                        ease: "easeOut"
                                                                    }}
                                                                    whileHover={{ 
                                                                        scale: 1.02,
                                                                        backgroundColor: "#1A1A1A",
                                                                        transition: { duration: 0.2 }
                                                                    }}
                                                                    whileTap={{ scale: 0.98 }}
                                                                    className="flex items-center justify-between p-2 rounded-md cursor-pointer"
                                                    >
                                                                    <div className="flex-1 min-w-0" onClick={() => {
                                                            navigate(`/prompts/edit/${prompt._id}`);
                                                            setDrawerVisible(false);
                                                        }}>
                                                            <div className="text-[12px] text-white truncate">
                                                                {prompt.title}
                                                            </div>
                                                            <div className="text-[10px] text-[#8E8E93] truncate">
                                                                {prompt.description || 'No description'}
                                                            </div>
                                                        </div>
                                                                </motion.div>
                                                ))}
                                            </div>
                                        )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Prompt Library Section - Temporarily hidden for MVP */}
                                    {/* <div className="p-4 border-b border-[#1A1A1A]">
                                        <div className="flex flex-row items-center justify-between w-full">
                                            <span className="text-[12px] text-white">Prompt Library</span>
                                            <motion.button 
                                                onClick={() => { 
                                                    navigate('/prompt-library');
                                                    setDrawerVisible(false); 
                                                }}
                                                whileHover={{ 
                                                    backgroundColor: "#007AFF",
                                                    color: "#FFFFFF"
                                                }}
                                                whileTap={{ scale: 0.98 }}
                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                className="text-[#007AFF] text-[10px] px-2 py-1 rounded border border-[#007AFF]"
                                            >
                                                Manage
                                            </motion.button>
                                        </div>
                                    </div> */}

                                    {/* Apps Section - Collapsible */}
                                    <div className="p-4">
                                        <div className="flex flex-row items-center justify-between w-full mb-4">
                                            <motion.div 
                                                className="flex flex-row items-center gap-x-[15px] cursor-pointer"
                                                onClick={() => setIsAppsExpanded(!isAppsExpanded)}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                <span className="text-[12px] text-white">Apps</span>
                                                <motion.div
                                                    animate={{ rotate: isAppsExpanded ? 90 : 0 }}
                                                    transition={{ duration: 0.2, ease: "easeInOut" }}
                                                    className="ml-1"
                                                >
                                                    <svg className="w-3 h-3 text-[#8E8E93]" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </motion.div>
                                            </motion.div>
                                        </div>
                                        
                                        <AnimatePresence initial={false}>
                                            {isAppsExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ 
                                                        duration: 0.3, 
                                                        ease: "easeInOut",
                                                        opacity: { duration: 0.2 }
                                                    }}
                                                    style={{ overflow: "hidden" }}
                                                >
                                                    <div className="space-y-3">
                                                        {/* Existing Apps */}
                                                        <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.1, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#FFFF00" />
                                                                <span className="text-[11px] text-white">Bookings</span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/bookings/view-all');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div>

                                                        <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.2, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#17A2B8" />
                                                                <span className="text-[11px] text-white">Catalog</span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/catalog/view-all');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div>

                                                        <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.3, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#4285F4" />
                                                                <span className="text-[11px] text-white">Connectors</span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/connectors');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div>

                                                        <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.4, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#34C759" />
                                                                <span className="text-[11px] text-white">Analytics</span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/analytics');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div>

                                                        {/* Temporarily hidden apps for MVP - not ready for production */}
                                                        {/* Merch, Charting, Playlisting, Social Listening hidden */}
                                                        {/* <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.3, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#9932CC" />
                                                                <span className="text-[11px] text-white">Merch</span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/merch/view-all');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div>

                                                        <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.5, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#FF8C00" />
                                                                <span className="text-[11px] text-white">Charting</span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/charting');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div>

                                                        <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.6, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#7B68EE" />
                                                                <span className="text-[11px] text-white">Playlisting</span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/playlisting');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div>

                                                        <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.7, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#0099FF" />
                                                                <span className="text-[11px] text-white">Social Listening</span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/social-listening');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div> */}

                                                        <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.5, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#5856D6" />
                                                                <span className="text-[11px] text-white">Knowledge</span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/knowledge-base');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div>

                                                        <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.6, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#7C4DFF" />
                                                                <span className="text-[11px] text-white">Notes</span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/notes');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div>

                                                        <motion.div 
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.7, duration: 0.3 }}
                                                            className="flex items-center justify-between py-2"
                                                        >
                                                            <div className="flex items-center gap-x-[10px]">
                                                                <ActiveDot color="#FF3B30" />
                                                                <span className="text-[11px] text-white">
                                                                    Workflows ({activeRemindersCount})
                                                                </span>
                                                            </div>
                                                            <motion.button 
                                                                onClick={() => { 
                                                                    navigate('/flows');
                                                                    setDrawerVisible(false); 
                                                                }}
                                                                whileHover={{ backgroundColor: "#007AFF", color: "#FFFFFF" }}
                                                                whileTap={{ scale: 0.98 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                                className="text-[#007AFF] text-[9px] px-2 py-1 rounded border border-[#007AFF]"
                                                            >
                                                                View
                                                            </motion.button>
                                                        </motion.div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Pages Section - TEMPORARILY HIDDEN */}
                                    {/* <div className="p-4 border-t border-[#1A1A1A]">
                                        <div className="flex flex-row items-center justify-between w-full mb-4">
                                            <div className="flex flex-row items-center gap-x-[15px]">
                                                <span className="text-[12px] text-white">Pages</span>
                                                <ActiveDot color="#FF2300" />
                                            </div>
                                                                        <button 
                                onClick={() => { 
                                    navigate('/main');
                                    setDrawerVisible(false); 
                                }}
                                className="text-[#007AFF] text-[10px] px-2 py-1 rounded border border-[#007AFF] hover:bg-[#007AFF] hover:text-white transition-colors"
                            >
                                Build
                            </button>
                                        </div>

                                        <div className="flex flex-col gap-y-[8px]">
                                            <DropdownItem onClick={() => navigate('/main')}>Brand Page</DropdownItem>
                                            <DropdownItem onClick={() => navigate('/focus-page')}>Focus Page</DropdownItem>
                                            {
                                                focusPageList?.filter(item => item && item.page).map((item, key) => (
                                                    <DropdownItem className='' key={key} onClick={() => navigateItem(item)}>{item.page}</DropdownItem>
                                                ))
                                            }
                                        </div>
                            </motion.div> */}
                                </div>
                                
                                {/* AI Mode Toggle Section */}
                                <div className="px-4 py-3 border-t border-[#1A1A1A] flex-shrink-0">
                                    <div className="flex items-center justify-between">
                                        <div className="flex flex-col">
                                            <span className="text-[12px] text-white mb-1">AI Mode</span>
                                            <span className="text-[10px] text-[#8E8E93]">
                                                {aiModeEnabled ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </div>
                                        <motion.button
                                            onClick={() => {
                                                const newValue = !aiModeEnabled;
                                                setPendingAIModeChange(newValue);
                                                setShowAIModeModal(true);
                                            }}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                                aiModeEnabled ? 'bg-[#FF3B30]' : 'bg-[#2A2A2A]'
                                            }`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                    aiModeEnabled ? 'translate-x-6' : 'translate-x-1'
                                                }`}
                                            />
                                        </motion.button>
                                    </div>
                                </div>
                                
                                {/* White Mode Toggle Section */}
                                <div className="px-4 py-3 border-t border-[#1A1A1A] flex-shrink-0">
                                    <div className="flex items-center justify-between">
                                        <div className="flex flex-col">
                                            <span className="text-[12px] text-white mb-1">White Mode</span>
                                            <span className="text-[10px] text-[#8E8E93]">
                                                {accessibilitySettings.theme === 'light' ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </div>
                                        <motion.button
                                            onClick={() => {
                                                const newTheme = accessibilitySettings.theme === 'light' ? 'dark' : 'light';
                                                const newSettings = { ...accessibilitySettings, theme: newTheme };
                                                setAccessibilitySettings(newSettings);
                                                localStorage.setItem('dizee-accessibility-settings', JSON.stringify(newSettings));
                                                applyAccessibilitySettings(newSettings);
                                            }}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                                accessibilitySettings.theme === 'light' ? 'bg-white border border-[#E5E5E5]' : 'bg-[#2A2A2A]'
                                            }`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                                                    accessibilitySettings.theme === 'light' ? 'translate-x-6 bg-[#000000]' : 'translate-x-1 bg-white'
                                                }`}
                                            />
                                        </motion.button>
                                    </div>
                                </div>
                                
                                {/* Feedback Section - Now at bottom above profile */}
                                <div className="px-4 py-2 border-t border-[#1A1A1A] flex-shrink-0">
                                    <div className="flex flex-row items-center justify-between w-full mb-2">
                                        <span 
                                            onClick={() => setFeedbackOpen(!feedbackOpen)}
                                            className="text-[12px] text-white cursor-pointer hover:opacity-80 transition-opacity"
                                        >
                                            Feedback
                                        </span>
                                        <button 
                                            onClick={() => setFeedbackOpen(!feedbackOpen)}
                                            className="w-[24px] h-[24px] bg-[#FF2300] rounded-[4px] flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                                        >
                                            <span className="text-white text-[12px]">?</span>
                                        </button>
                                    </div>
                                    
                                    <AnimatePresence initial={false}>
                                        {feedbackOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ 
                                                    duration: 0.3, 
                                                    ease: "easeInOut",
                                                    opacity: { duration: 0.2 }
                                                }}
                                                style={{ overflow: "hidden" }}
                                                className="mt-2"
                                            >
                                                <motion.div
                                                    initial={{ y: -10, opacity: 0 }}
                                                    animate={{ y: 0, opacity: 1 }}
                                                    transition={{ delay: 0.1, duration: 0.3, ease: "easeOut" }}
                                                >
                                                    <div className="text-[12px] text-white mb-2">How can we improve?</div>
                                                    {!feedbackSubmitted ? (
                                                        <>
                                                            <textarea
                                                                value={feedback}
                                                                onChange={(e) => setFeedback(e.target.value)}
                                                                className="w-full h-[80px] bg-[#1A1A1A] text-white text-[12px] p-2 rounded border border-[#2A2A2A] focus:border-[#FF2300] focus:outline-none resize-none transition-colors"
                                                                placeholder="Type your feedback here..."
                                                            />
                                                            <div className="flex justify-end mt-2">
                                                                <button
                                                                    onClick={handleFeedbackSubmit}
                                                                    disabled={feedbackSubmitting || !feedback.trim()}
                                                                    className={`text-white text-[12px] px-3 py-1 rounded bg-[#FF2300] hover:opacity-90 transition-opacity 
                                                                        ${(feedbackSubmitting || !feedback.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                >
                                                                    {feedbackSubmitting ? 'Submitting...' : 'Submit'}
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="text-white text-[12px] text-center py-4 bg-[#1A1A1A] rounded">
                                                            Thank you for your feedback!
                                                        </div>
                                                    )}
                                                </motion.div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                                
                                {/* Profile Section - Always visible at bottom */}
                                <motion.div 
                                    variants={{
                                        hidden: { opacity: 0, y: 20 },
                                        visible: { 
                                            opacity: 1, 
                                            y: 0,
                                            transition: { duration: 0.5, ease: "easeOut" }
                                        }
                                    }}
                                    className="px-4 pt-4 pb-2 border-t border-[#1A1A1A] bg-black flex-shrink-0"
                                >
                                    <motion.div 
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                        className="flex flex-row items-center justify-between gap-x-[15px] mb-6 cursor-pointer"
                                        onClick={() => setIsProfileMenuExpanded(!isProfileMenuExpanded)}
                                    >
                                        <div className="flex flex-row items-center gap-x-[15px]">
                                            <motion.img 
                                                whileHover={{ scale: 1.1 }}
                                                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                                                src={getProfileImage()} 
                                                className='w-[40px] h-[40px] rounded-full' 
                                                alt="" 
                                            />
                                        <div className="flex flex-col">
                                            <div className="flex items-center">
                                                <span className="text-[14px] font-medium">{userArticle.username}</span>
                                                <BetaBadge>Beta invite only</BetaBadge>
                                            </div>
                                            <span className="text-[12px] text-[#8E8E93]">diz.ee/{userArticle?.domain || user?.domain}</span>
                                        </div>
                                    </div>
                                        <motion.div
                                            animate={{ rotate: isProfileMenuExpanded ? 90 : 0 }}
                                            transition={{ duration: 0.2, ease: "easeInOut" }}
                                            className="ml-1"
                                        >
                                            <svg className="w-4 h-4 text-[#8E8E93]" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </motion.div>
                                    </motion.div>
                                    <AnimatePresence initial={false}>
                                        {isProfileMenuExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ 
                                                    duration: 0.3, 
                                                    ease: "easeInOut",
                                                    opacity: { duration: 0.2 }
                                                }}
                                                style={{ overflow: "hidden" }}
                                                className="mb-6"
                                            >
                                                <div className="flex flex-col gap-y-[6px]">
                                                    {/* Team link temporarily hidden - feature coming soon
                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: 0.1, duration: 0.3 }}
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        style={{ transition: "all 0.2s ease" }}
                                                    >
                                        <StyledDropdownItem onClick={() => navigate('/more/setting/advanced/team-management')} style={{ textDecoration: 'none' }}>Team</StyledDropdownItem>
                                                    </motion.div>
                                                    */}
                                                    {/* Accessibility Section */}
                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: 0.1, duration: 0.3 }}
                                                        style={{ transition: "all 0.2s ease" }}
                                                    >
                                                        <StyledDropdownItem 
                                                            onClick={() => setIsAccessibilityExpanded(!isAccessibilityExpanded)}
                                                            style={{ 
                                                                textDecoration: 'none'
                                                            }}
                                                        >
                                                            <span>Accessibility</span>
                                                        </StyledDropdownItem>
                                                        
                                                        {/* Accessibility Controls */}
                                                        {isAccessibilityExpanded && (
                                                            <motion.div
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: "auto", opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                transition={{ duration: 0.2 }}
                                                                style={{ overflow: 'hidden', paddingLeft: '10px', marginTop: '8px' }}
                                                            >
                                                                <div style={{ fontSize: '10px', color: '#8E8E93', marginBottom: '6px', fontWeight: '500' }}>Font Size</div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginBottom: '12px' }}>
                                                                    {['small', 'medium', 'large', 'xlarge'].map((size) => (
                                                                        <button
                                                                            key={size}
                                                                            onClick={() => updateAccessibilitySetting('fontSize', size)}
                                                                            style={{
                                                                                padding: '4px',
                                                                                fontSize: '9px',
                                                                                borderRadius: '4px',
                                                                                border: `1px solid ${accessibilitySettings.fontSize === size ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                                                                                backgroundColor: accessibilitySettings.fontSize === size ? '#3b82f6' : 'rgba(255,255,255,0.05)',
                                                                                color: 'white',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.2s'
                                                                            }}
                                                                        >
                                                                            {size === 'xlarge' ? 'XL' : size.charAt(0).toUpperCase()}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                                
                                                                <div style={{ fontSize: '10px', color: '#8E8E93', marginBottom: '6px', fontWeight: '500' }}>Theme</div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px', marginBottom: '12px' }}>
                                                                    {[
                                                                        { value: 'light', label: 'Light' },
                                                                        { value: 'dark', label: 'Dark' },
                                                                        { value: 'grayscale', label: 'Gray' },
                                                                    ].map(({ value, label }) => (
                                                                        <button
                                                                            key={value}
                                                                            onClick={() => updateAccessibilitySetting('theme', value)}
                                                                            style={{
                                                                                padding: '4px',
                                                                                fontSize: '9px',
                                                                                borderRadius: '4px',
                                                                                border: `1px solid ${accessibilitySettings.theme === value ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                                                                                backgroundColor: accessibilitySettings.theme === value ? '#3b82f6' : 'rgba(255,255,255,0.05)',
                                                                                color: 'white',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.2s'
                                                                            }}
                                                                        >
                                                                            {label}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                                
                                                                <button
                                                                    onClick={() => updateAccessibilitySetting('highContrast', !accessibilitySettings.highContrast)}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '6px',
                                                                        fontSize: '10px',
                                                                        borderRadius: '4px',
                                                                        border: `1px solid ${accessibilitySettings.highContrast ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                                                                        backgroundColor: accessibilitySettings.highContrast ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.05)',
                                                                        color: 'white',
                                                                        cursor: 'pointer',
                                                                        marginBottom: '6px',
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    <span>High Contrast</span>
                                                                    <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.7)' }}>{accessibilitySettings.highContrast ? 'ON' : 'OFF'}</span>
                                                                </button>
                                                                
                                                                <button
                                                                    onClick={() => updateAccessibilitySetting('dyslexicFont', !accessibilitySettings.dyslexicFont)}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '6px',
                                                                        fontSize: '10px',
                                                                        borderRadius: '4px',
                                                                        border: `1px solid ${accessibilitySettings.dyslexicFont ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                                                                        backgroundColor: accessibilitySettings.dyslexicFont ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.05)',
                                                                        color: 'white',
                                                                        cursor: 'pointer',
                                                                        marginBottom: '6px',
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    <span>Dyslexia Font</span>
                                                                    <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.7)' }}>{accessibilitySettings.dyslexicFont ? 'ON' : 'OFF'}</span>
                                                                </button>
                                                                
                                                                <button
                                                                    onClick={() => updateAccessibilitySetting('reduceMotion', !accessibilitySettings.reduceMotion)}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '6px',
                                                                        fontSize: '10px',
                                                                        borderRadius: '4px',
                                                                        border: `1px solid ${accessibilitySettings.reduceMotion ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                                                                        backgroundColor: accessibilitySettings.reduceMotion ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.05)',
                                                                        color: 'white',
                                                                        cursor: 'pointer',
                                                                        marginBottom: '8px',
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    <span>Reduce Motion</span>
                                                                    <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.7)' }}>{accessibilitySettings.reduceMotion ? 'ON' : 'OFF'}</span>
                                                                </button>
                                                                
                                                                <button
                                                                    onClick={resetAccessibilitySettings}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '6px',
                                                                        fontSize: '9px',
                                                                        borderRadius: '4px',
                                                                        border: '1px solid rgba(255,255,255,0.1)',
                                                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                                                        color: 'white',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    Reset to Default
                                                                </button>
                                                            </motion.div>
                                                        )}
                                                    </motion.div>
                                                    
                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: 0.2, duration: 0.3 }}
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        style={{ transition: "all 0.2s ease" }}
                                                    >
                                        <StyledDropdownItem onClick={() => navigate('/more/setting')} style={{ textDecoration: 'none' }}>Settings</StyledDropdownItem>
                                                    </motion.div>
                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: 0.3, duration: 0.3 }}
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        style={{ transition: "all 0.2s ease" }}
                                                    >
                                        <StyledDropdownItem onClick={handleLogout}>Logout</StyledDropdownItem>
                                                    </motion.div>
                                    </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            </motion.div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
            
            {/* AI Mode Modal */}
            <AIModeModal
                isOpen={showAIModeModal}
                onClose={() => {
                    setShowAIModeModal(false);
                    setPendingAIModeChange(null);
                }}
                onConfirm={handleAIModeToggle}
                isTurningOff={pendingAIModeChange === false}
            />
        </div>
    )
}

export default HeaderBar;
